import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const seasons = [
    { name: "School", images: ["school.png"] },
    { name: "Hospital", images: ["hospital.png"] },
    { name: "Park", images: ["park.png"] },
    { name: "Police Station", images: ["policestation.png"] },
    { name: "Fire Station", images: ["firestation.png"] },
    { name: "Post Office", images: ["postoffice.png"] },
    { name: "Library", images: ["library.png"] },
    { name: "Bank", images: ["bank.png"] },
    { name: "Market", images: ["market.png"] },
    { name: "Zoo", images: ["zoo.png"] },
    { name: "Museum", images: ["museum.png"] },
    { name: "Bus Stop", images: ["busstop.png"] },
    { name: "Train Station", images: ["trainstation.png"] },
    { name: "Airport", images: ["airport.png"] },
];

const getRandomUniqueOptions = (correctOption: { name: any; images?: string[]; }) => {
    const options = [correctOption];
    const availableOptions = seasons.filter(season => season.name !== correctOption.name);
    while (options.length < 4) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const GuessArea = () => {
    const getNewQuestion = () => {

        document.title = "Guess the Neighbourhood Area – Nursery Worksheet | Mama’s Guidance"; // Set page title
        const newQuestion = seasons[Math.floor(Math.random() * seasons.length)];
        const randomImage = newQuestion.images[Math.floor(Math.random() * newQuestion.images.length)];
        return { newQuestion, newImage: randomImage, newOptions: getRandomUniqueOptions(newQuestion) };
    };

    const [{ newQuestion: question, newImage: image, newOptions: options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            setShowImage(true);
            setTimeout(() => {
                setQuizState(getNewQuestion());
                setFeedback("");
                setShowImage(false);
            }, 3000);
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {

            setQuizState(getNewQuestion());
            setFeedback("");
            setShowImage(false);

        }, 5000)
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids learn about their surroundings with the "Guess the Area" activity on Mamas Guidance—an interactive game that teaches children to identify different areas in the neighborhood, promoting general awareness and spatial understanding.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Guess the Neighbourhood Area</h1>

                                    <p>
                                        Help your child explore the places around them with this exciting <strong>Guess the Area</strong> worksheet! It’s a simple and fun general awareness activity that encourages children to <strong>identify common places in their neighbourhood</strong> through pictures and clues.
                                    </p>

                                    <h2>🏥 What’s Inside?</h2>
                                    <p>
                                        The worksheet shows images or simple descriptions of areas like a <b><em>park, hospital, school, fire station, market, police station, post office</em></b>, and more. Children must <strong>guess which neighbourhood place is shown</strong> and choose the correct answer.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Recognize important neighbourhood places and buildings</li>
                                        <li>Understand what happens at each place (e.g., study, play, treatment)</li>
                                        <li>Improve observation and logical reasoning</li>
                                        <li>Enhance vocabulary and community awareness</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Show your child the picture or read the clue aloud.</li>
                                        <li>Ask them to name the place shown and describe what they know about it.</li>
                                        <li>Let them circle or choose the correct name of the area.</li>
                                        <li>Talk about where they’ve seen this place in real life.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/general-awareness/transport-mode/guess-the-transport"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess the Transport</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Pick Color</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess Community Helper</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Matters</h2>
                                    <p>
                                        Knowing about the neighbourhood helps children <strong>feel connected to their surroundings</strong>. It promotes safety, independence, and awareness of different roles and places in a community.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Guess the Neighbourhood Area</h1>

                                <p>
                                    Help your child explore the places around them with this exciting <strong>Guess the Area</strong> worksheet! It’s a simple and fun general awareness activity that encourages children to <strong>identify common places in their neighbourhood</strong> through pictures and clues.
                                </p>

                                <h2>🏥 What’s Inside?</h2>
                                <p>
                                    The worksheet shows images or simple descriptions of areas like a <b><em>park, hospital, school, fire station, market, police station, post office</em></b>, and more. Children must <strong>guess which neighbourhood place is shown</strong> and choose the correct answer.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize important neighbourhood places and buildings</li>
                                    <li>Understand what happens at each place (e.g., study, play, treatment)</li>
                                    <li>Improve observation and logical reasoning</li>
                                    <li>Enhance vocabulary and community awareness</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show your child the picture or read the clue aloud.</li>
                                    <li>Ask them to name the place shown and describe what they know about it.</li>
                                    <li>Let them circle or choose the correct name of the area.</li>
                                    <li>Talk about where they’ve seen this place in real life.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/general-awareness/transport-mode/guess-the-transport"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess the Transport</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Pick Color</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess Community Helper</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Matters</h2>
                                <p>
                                    Knowing about the neighbourhood helps children <strong>feel connected to their surroundings</strong>. It promotes safety, independence, and awareness of different roles and places in a community.
                                </p>
                            </article>
                            <div className="mt-4">
                                <img src={process.env.REACT_APP_API_URL + image} className="season-image helper-image" />
                            </div>
                            <div className="mt-4" style={{ display: 'ruby' }}>
                                {options.map((option, index) => (
                                    <div key={index} style={{ margin: '10px' }}>
                                        <button className="optionButton background-blue" onClick={() => handleAnswer(option.name)}>{option.name}</button>
                                    </div>
                                ))}
                            </div>
                            <h3>{feedback}</h3>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GuessArea;
